import React, { useEffect, useState } from "react";
import { FlaskConical, Info, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const TYPE_COLOR = {
  sativa: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  indica: "text-violet-300 bg-violet-500/10 border-violet-500/30",
  hybrid: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
};

/**
 * Strain Info chip + modal for product cards. One tap opens a modal with
 * the strain description, effects, flavors, AND terpene profile — a mini
 * education moment that keeps users on the results page instead of losing
 * them to /strains/<slug>. Fetch is lazy (only on first open), cached on
 * the component instance so re-opens are instant.
 */
export const TerpeneChipButton = ({ strainSlug, strainName, productId }) => {
  const [open, setOpen] = useState(false);
  const [strain, setStrain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || strain || loading) return;
    setLoading(true);
    setError(null);
    api
      .get(`/strains/${strainSlug}`)
      .then((r) => setStrain(r.data?.strain || null))
      .catch(() => setError("Couldn't load strain info."))
      .finally(() => setLoading(false));
  }, [open, strainSlug, strain, loading]);

  if (!strainSlug) return null;

  const terpenes = strain?.terpenes;

  return (
    <>
      <button
        type="button"
        data-testid={`terpene-chip-${productId}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
          trackEvent("strain_info_open", productId);
        }}
        className="inline-flex items-center gap-1.5 h-7 rounded-full pl-2.5 pr-3 text-[11px] font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-colors"
      >
        <Info className="w-3 h-3" strokeWidth={2.2} />
        Strain Info
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={`terpene-modal-${productId}`}
          className="bg-card border-border max-w-md max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              {strainName || "Strain info"}
              {strain?.type && (
                <span
                  className={`text-[9px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full border ${
                    TYPE_COLOR[strain.type] || ""
                  }`}
                >
                  {strain.type}
                </span>
              )}
            </DialogTitle>
            {strain?.description && (
              <DialogDescription className="text-sm text-foreground/75 leading-relaxed pt-1">
                {strain.description}
              </DialogDescription>
            )}
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-8 text-foreground/60">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading strain info…
            </div>
          )}

          {error && !loading && (
            <div className="text-sm text-destructive py-4">{error}</div>
          )}

          {!loading && !error && strain && (
            <div className="space-y-5">
              {/* Effects + Flavors */}
              {(strain.effects?.length || strain.flavors?.length) && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {strain.effects?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-foreground/50 mb-2">
                        Effects
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {strain.effects.map((e) => (
                          <span
                            key={e}
                            className="pill capitalize text-[11px]"
                          >
                            {e}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {strain.flavors?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-foreground/50 mb-2">
                        Flavor profile
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {strain.flavors.map((f) => (
                          <span
                            key={f}
                            className="pill capitalize text-[11px]"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Terpene profile */}
              {terpenes && (
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FlaskConical
                        className="w-4 h-4 text-emerald-300"
                        strokeWidth={1.8}
                      />
                      <div className="text-[10px] uppercase tracking-wider text-foreground/70">
                        Typical terpene profile
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-foreground/50">
                      Dominant ·{" "}
                      <span className="text-emerald-300">
                        {terpenes.dominant}
                      </span>
                    </span>
                  </div>
                  <div className="space-y-3">
                    {terpenes.profile.map((t) => (
                      <div key={t.name} data-testid={`terpene-${t.name.toLowerCase()}`}>
                        <div className="flex items-baseline justify-between text-sm mb-1">
                          <span className="font-medium text-foreground/90">
                            {t.name}
                          </span>
                          <span className="text-xs text-foreground/60 tabular-nums">
                            {Math.round(t.share * 100)}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-background rounded-full overflow-hidden border border-border/60">
                          <div
                            className="h-full bg-emerald-500/70 rounded-full"
                            style={{ width: `${Math.round(t.share * 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-foreground/60 mt-1.5 leading-relaxed">
                          <span className="text-foreground/50">Aroma:</span>{" "}
                          {t.aroma}
                          <span className="mx-1.5 text-foreground/30">·</span>
                          <span className="text-foreground/50">Effects:</span>{" "}
                          {t.effects}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-foreground/50 mt-4 leading-relaxed">
                    {terpenes.source === "curated"
                      ? "Typical terpene profile based on public strain data. Check the dispensary's batch lab report for exact percentages."
                      : "Generic terpene profile based on strain type — no curated data yet. Ask your dispensary for the batch-specific lab report."}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TerpeneChipButton;
